import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { GraphService } from './graph.service';

interface PassengerFields {
  id?: boolean;
  address?: boolean;
  age?: boolean;
  alergias?: boolean;
  city?: boolean;
  country?: boolean;
  doe?: boolean;
  email?: boolean;
  firstName?: boolean;
  gender?: boolean;
  height?: boolean;
  idItinerary?: boolean;
  isTC?: boolean;
  lastName?: boolean;
  passportNumber?: boolean;
  phone?: boolean;
  restriccionesAlimentarias?: boolean;
  condicionesMedicas?: boolean;
  size?: boolean;
  status?: boolean;
  weight?: boolean;
  createdAt?: boolean;
  updatedAt?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class SalesService {
  constructor(private readonly graphService: GraphService) {}

  getPassengersByIds({
    passengerIds = [],
    passengerFields = {},
  }: {
    passengerIds: string[];
    passengerFields?: PassengerFields;
  }) {
    if (passengerIds.length === 0) {
      throw new Error('passengerIds array cannot be empty');
    }
    const fieldsString = Object.keys(passengerFields || {})
      .filter((key) => passengerFields[key as keyof typeof passengerFields]) // Only include fields that are true
      .join(', ');

    // Fallback if no fields are specified, though your requirements imply usage of 'fields'
    const finalFields = fieldsString || 'id, firstName, lastName';

    return this.graphService.queryGraphQLSales<any>({
      query: `query GetPassengersByIds (
        $passengerIds: [String!]!,
        $passengerFields: String
      ) {
        getPassengers(passengerIds: $passengerIds, passengerFields: $passengerFields) {
          ${finalFields}
        }
      }`,
      variables: { passengerIds, passengerFields: finalFields },
    });
  }

  getAssignedPassengers({
    itineraryId,
    passengerFields = {},
  }: {
    itineraryId: string;
    passengerFields?: PassengerFields;
  }) {
    const fieldsString = Object.keys(passengerFields || {})
      .filter((key) => passengerFields[key as keyof typeof passengerFields]) // Only include fields that are true
      .join(', ');

    // Fallback if no fields are specified, though your requirements imply usage of 'fields'
    const finalFields = fieldsString || 'id, firstName, lastName';

    return this.graphService.queryGraphQLSales({
      query: `query GetAssignedPassengers (
        $itineraryId: String!,
        $passengerFields: String
      ) {
        getAssignedPassengers(itineraryId: $itineraryId, passengerFields: $passengerFields) {
          ${finalFields}
        }
      }`,
      variables: { itineraryId, passengerFields: finalFields },
    });
  }
  getAllFiles(): Observable<any> {
    return this.graphService.queryGraphQLSales<any>({
      query: `query{
        getAllFiles{
            id,
            fileNumber,
           nombreItinerario,
           dateRange
          }
      }`,
      variables: {},
    });
  }

  /**
   * Retrieves the catalog of active quotation file numbers with extensions from Sales GraphQL API.
   *
   * Description:
   *   Fetches quotation files and sorts them by default in descending alphabetical/numerical order (Z -> A).
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   Observable<{ getAllFilesNumbers: { id: string; fileNumber: string; fileExtend?: string | null }[] }> -
   *   Observable emitting the list of files sorted descending.
   *
   * Exceptions & Errors:
   *   Propagates GraphQL communication errors through the Observable error channel.
   */
  getAllFilesNumbers(): Observable<{
    getAllFilesNumbers: {
      id: string;
      fileNumber: string;
      fileExtend?: string | null;
    }[];
  }> {
    return this.graphService
      .queryGraphQLSales<{
        getAllFilesNumbers: {
          id: string;
          fileNumber: string;
          fileExtend?: string | null;
        }[];
      }>({
        query: `query {
          getAllFilesNumbers {
            id
            fileNumber
            fileExtend
          }
        }`,
        variables: {},
      })
      .pipe(
        map((responsePayload) => {
          const filesCatalog = responsePayload?.getAllFilesNumbers || [];
          const sortedFilesCatalog = [...filesCatalog].sort(
            (firstItem, secondItem) => {
              const numberComparison = (
                secondItem?.fileNumber || ''
              ).localeCompare(firstItem?.fileNumber || '');
              if (numberComparison !== 0) {
                return numberComparison;
              }
              return (secondItem?.fileExtend || '').localeCompare(
                firstItem?.fileExtend || '',
              );
            },
          );
          return {
            ...responsePayload,
            getAllFilesNumbers: sortedFilesCatalog,
          };
        }),
      );
  }

  queryGetAllFilesWithServicesAndMenu(tipoServicio: any, key?: string) {
    return this.graphService.queryGraphQLSales<any>({
      query: `query GetAllFilesWithServicesAndMenu($tipoServicio:String!, $key: String) {
        getAllFilesWithServicesAndMenu(tipoServicio: $tipoServicio, key: $key) {
          isThereMore,
          LastEvaluatedKey,
          resultFiles {
            id,
            fileDisplayState,
            tcQuantity,
            estadoFile,
            cantPersonasAB,
            nombreItinerario,
            observaciones
            dateRange,
            egresoInsumos,
            insumos {
              id
              cantidad
              unidadMedida
              nombreInsumo
              service{
                nombreServicio
              }
                menu{
                nombreMenu
                }
            }
            services {
              id,
              day,
              confirmInsumosAYB,
              confirmPDFAYB,
              observacionAYB,
              totalesPersonalCampoAYB,
              cantPersonal,
              fechaServicio,
              productId,
              objProducto {
                id
                nombre
              }
              menu {
                descripcion,
                nombreMenu,
                costoMenu,
                id
              }
              nroPersonasPax {
                id,
                numPax,
              }
              lstPaX {
                flightIds
                Pax
                estado
                grupo
                grupoId
                acumulado
                paxMenu {
                  menu {
                    id
                    nombreMenu
                  }
                  observaciones
                }
                type
                passengerId
              }
              serviceId,
              nombreServicio
            }
            fileName,
            fileNumber,
            lstPaX {
              flightIds
              Pax
              estado
              grupo
              grupoId
              acumulado
              paxMenu {
                menu {
                  id
                  nombreMenu
                }
                observaciones
              }
              type
              passengerId
            }
          }
        }
      }`,
      variables: { tipoServicio, key },
    });
  }
  updateServiceItinerary(service: any): Observable<any> {
    return this.graphService.mutateGraphQLSales<any>({
      mutation: `mutation updateServiceItinerary( $service: inputServices_Dto_extended!){
        updateServiceItinerary(service: $service){
          id,
        }
      }`,
      variables: { service },
    });
  }

  getValidServicesFilter(idItinerary: any, tipoServicio: any) {
    return this.graphService.queryGraphQLSales<any>({
      query: `query GetValidServicesFilter($idItinerary:String!,$tipoServicio:String!){
        GetValidServicesFilter(idItinerary:$idItinerary,tipoServicio:$tipoServicio){
          id,
          nombreServicio
          serviceId,
          tipoServicio,
          categoriaServicio,
          providerId

        }
      }`,
      variables: { idItinerary, tipoServicio },
    });
  }

  getServicesByFile(itineraryId: any) {
    return this.graphService.queryGraphQLSales<any>({
      query: `query getServicesByFile ($id:String!) {
        getServicesByFile (id:$id) {
          id,
          day,
          dateRange,
          lstPaX {
            flightIds
            Pax
            estado
            grupo
            grupoId
            acumulado
            paxMenu {
              menu {
                id
                nombreMenu
              }
              observaciones
            }
            type
            passengerId
          }
          serviceId,
          nombreServicio,
          objProvider{
            id,
            nombre
          }
          tipoServicio
          categoriaServicio
        }
      }`,
      variables: { id: itineraryId },
    });
  }

  updateItinerary(id: string, itinerary: any): Observable<any> {
    return this.graphService.mutateGraphQLSales<any>({
      mutation: `mutation updateItinerary($id: String!, $itinerary: inputItinerary_Dto!){
        updateItinerary(id: $id, itinerary: $itinerary){
          id
        }
      }`,
      variables: { id, itinerary },
    });
  }

  updateInsumosAdicionales(
    id: string,
    insumo: any,
  ): Observable<{ updateInsumosAdicionales: boolean }> {
    return this.graphService.mutateGraphQLSales<{
      updateInsumosAdicionales: boolean;
    }>({
      mutation: `mutation updateInsumosAdicionales($id: String!, $insumo: inputInsumo!){
        updateInsumosAdicionales(id: $id, insumo: $insumo)
      }`,
      variables: { id, insumo },
    });
  }

  /**
   * Updates the assigned provider for an itinerary service in Sales.
   *
   * @param idRegistro - Primary key UUID of the sales-itinerario-services record.
   * @param idProvider - Primary key UUID of the selected Sourcing provider.
   * @param user - Email identifier of the authenticated user.
   * @param servicioId - Primary key UUID of the Sourcing catalog service.
   * @returns Observable with mutation response payload containing success status and message.
   * @throws Error on network failure or GraphQL execution rejection.
   */
  public changeProvider(
    idRegistro: string,
    idProvider: string,
    user: string,
    servicioId: string = '',
  ): Observable<{ changeProvider: { success: boolean; message: string } }> {
    return this.graphService.mutateGraphQLSales<{
      changeProvider: { success: boolean; message: string };
    }>({
      mutation: `
        mutation ChangeProvider(
          $idRegistro: String!
          $idProvider: String!
          $user: String!
          $servicioId: String!
        ) {
          changeProvider(
            idRegistro: $idRegistro
            idProvider: $idProvider
            user: $user
            servicioId: $servicioId
          ) {
            success
            message
          }
        }
      `,
      variables: { idRegistro, idProvider, user, servicioId },
    });
  }
}
